import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  darken,
  Divider,
  Grid,
  LinearProgress,
  Link,
  Typography,
} from "@mui/material";
import React, { ReactNode } from "react";
import { memberTheme } from "../memberTheme";
import { getJourneyContinuation, getThreeJourneys } from "@flow/common/core_routes";
import { MainContainer } from "./MainContainer";
import { JourneyContinuation } from "@flow/common/types";
import { calculateActionsTarget } from "@flow/common/logic/action_helpers";
import { formatBigNumber } from "@flow/plugins/petition/frontend/member/PetitionActionComponent";
import { ActionComponentWrapper } from "./ActionComponentWrapper";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { MemberLoadingSpinner } from "./MemberLoadingSpinner";

interface JourneyContinuationProps {
  children?: ReactNode;
  email?: string;
  journey_name: string;
}

function JourneyContinuation({ children, email, journey_name }: JourneyContinuationProps) {
  const [journeyContinuation, setJourneyContinuation] = React.useState<JourneyContinuation[] | undefined>();
  const [, setHovered] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  {
    email &&
      React.useEffect(() => {
        getJourneyContinuation.call({ email }).then((journeys) => {
          const numberOfJourneys = journeys.length;
          if (numberOfJourneys < 3) {
            setLoading(true);
            getThreeJourneys.call().then((threeJourneys) => {
              const allJourneys = [...journeys, ...threeJourneys];
              const uniqueJourneys = removeDuplicates(allJourneys);
              const finalJourneys = uniqueJourneys.slice(0, 3);
              setJourneyContinuation(finalJourneys);
              setLoading(false);
            });
          } else {
            setJourneyContinuation(journeys.slice(0, 3));
          }
        });
      }, []);
  }

  function removeDuplicates(journeys: { og_image: string; name: string; slug: string; signature_count: number }[]) {
    const uniqueJourneys = [];
    const slugSet = new Set();

    for (const journey of journeys) {
      if (!slugSet.has(journey.slug)) {
        uniqueJourneys.push(journey);
        slugSet.add(journey.slug);
      }
    }

    return uniqueJourneys;
  }

  const handleRefresh = () => {
    window.location.reload();
  };

  const formUrl = (slug: string) => {
    const currentUrl = window.location.href;
    const segments = currentUrl.split("/");
    segments.pop();
    const partialUrl = segments.join("/");
    const newUrl = partialUrl + "/" + slug;
    return encodeURI(newUrl);
  };

  const handleClick = (slug: string) => {
    const url = formUrl(slug);

    const urlObj = new URL(url);
    urlObj.searchParams.append("utm_source", window.location.hostname);
    urlObj.searchParams.append("utm_medium", "flow-journey-continuation");
    urlObj.searchParams.append("utm_campaign", journey_name);

    window.location.assign(urlObj.toString());
  };

  const darkenColor = (color: string) => darken(color, 0.1);

  return (
    <>
      <Typography
        fontWeight="bold"
        display={{ xs: "none", md: "block" }}
        sx={{
          typography: { xs: "subtitle1", md: "h4" },
          color: memberTheme.palette.primary.dark,
          marginBottom: "14px",
          marginRight: "2px",
        }}
      >
        Thank you for your support
      </Typography>
      <ActionComponentWrapper>
        <MainContainer>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              color: memberTheme.palette.primary.main,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: 700,
                }}
              >
                Campaigns for you
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  color: memberTheme.palette.primary.dark,
                  marginTop: "10px",
                }}
              >
                Campaigns that would benefit from your support.
              </Typography>
            </Box>
          </Box>
          <Divider
            variant="middle"
            sx={{ marginTop: "20px", marginBottom: "20px", marginLeft: "0px", marginRight: "0px" }}
          />

          {loading ? (
            <MemberLoadingSpinner />
          ) : (
            <>
              {journeyContinuation &&
                journeyContinuation.map((journey) => (
                  <Card
                    key={journey.name}
                    sx={{
                      marginBottom: "16px",
                      borderRadius: "4px",
                      boxShadow: "none",
                      position: "relative",
                      overflow: "hidden",
                      border: `1px solid ${memberTheme.palette.greys.grey300}`,
                      transition: "border-color 400ms ease-in-out",
                      "&:hover": {
                        borderColor: `${memberTheme.palette.greys.grey500}`,
                        cursor: "pointer",
                      },
                    }}
                    role="article"
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    onClick={() => handleClick(journey.slug)}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px",
                        backgroundColor: "#FFF",
                        border: "1px solid #E0E0E0)",
                        "&:hover": {
                          backgroundColor: `${memberTheme.palette.greys.grey100}`,
                        },
                      }}
                    >
                      <CardMedia
                        component="img"
                        sx={{ width: "101px", height: "102px", objectFit: "cover" }}
                        image={journey.og_image}
                        alt={journey.name}
                      />
                      <CardContent
                        sx={{
                          flexGrow: 1,
                          backgroundColor: memberTheme.palette.primary.grey50,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          paddingLeft: "10px",
                          paddingTop: "0px",
                          paddingBottom: {
                            xs: "0px",
                          },
                          height: {
                            xs: "102px",
                          },
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: "700",
                            lineHeight: "157%",
                            paddingBottom: { md: "10px" },
                            marginBottom: { xs: "5px" },
                            height: { xs: "44px" },
                            overflow: { xs: "hidden" },
                          }}
                        >
                          {journey.name}
                        </Typography>
                        <Box sx={{ flexGrow: 1 }}>
                          <SignatureProgressBar
                            count={journey.signature_count}
                            target={calculateActionsTarget(journey.signature_count)}
                          />
                        </Box>
                      </CardContent>
                    </Box>
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: "8px",
                        right: "8px",
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "space-between",
                        width: { md: "80%", xs: "58%" },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          color: memberTheme.palette.primary.grey,
                          paddingLeft: "8px",
                          typography: "subtitle2",
                          fontWeight: "bold",
                        }}
                      >
                        {formatBigNumber(journey.signature_count)} Actions Taken - Add Yours
                      </Box>
                    </Box>
                  </Card>
                ))}
              {children}
            </>
          )}

          <Divider
            variant="middle"
            sx={{ marginTop: "20px", marginBottom: "20px", marginLeft: "0px", marginRight: "0px" }}
          />
          <Grid>
            <Grid item xs={12} sx={{ marginBottom: "20px" }}>
              <Button
                variant="contained"
                href="https://home.38degrees.org.uk/campaigns/"
                sx={{
                  width: "100%",
                  height: "36px",
                  backgroundColor: memberTheme.palette.greys.grey300,
                  color: memberTheme.palette.secondary.dark,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: darkenColor(memberTheme.palette.greys.grey300),
                    color: darkenColor(memberTheme.palette.secondary.dark),
                    boxShadow: "none",
                  },
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                endIcon={<ChevronRightIcon sx={{ marginLeft: "8px" }} />}
              >
                Recent Campaigns
              </Button>
            </Grid>
            {email?.includes(`@38degrees.org.uk`) && (
              <Grid item xs={12} sx={{ textAlign: "center" }}>
                <Link
                  onClick={handleRefresh}
                  sx={{
                    width: "100%",
                    display: "block",
                    color: memberTheme.palette.blue.main,
                    "&:hover": {
                      cursor: "pointer",
                    },
                  }}
                >
                  Restart journey
                </Link>
              </Grid>
            )}
          </Grid>
        </MainContainer>
      </ActionComponentWrapper>
    </>
  );
}

function SignatureProgressBar({ count, target }: { count: number; target: number }) {
  const targetPercent = (100 * count) / target;
  return (
    <>
      <LinearProgress
        variant="determinate"
        value={targetPercent}
        sx={{
          width: "100%",
          height: "4px",
          borderRadius: "0",
        }}
        aria-label="Petition signatures"
        aria-valuenow={count}
        aria-valuemin={0}
        aria-valuemax={target}
      />
    </>
  );
}

export { JourneyContinuation };
